import React from "react";
import styled, { createGlobalStyle, css, type SimpleInterpolation } from "styled-components";
import { getStaticUrl } from "js/config";
import Spinner from "js/react/components/Spinner";

const postGroteskBook = getStaticUrl("/fonts/PostGrotesk-Book.ttf");
const postGroteskMedium = getStaticUrl("/fonts/PostGrotesk-Medium.ttf");
const robotoRegular = getStaticUrl("/fonts/Roboto/Roboto-Regular.ttf");
const beautifulAiLogo = getStaticUrl("/images/beautifulai-logos/beautifulai-logo.svg");

export const postGroteskFont = `"Post Grotesk", "Source Sans Pro", sans-serif`;

export function mediaMaxWidth(breakpoint: number) {
    return (
        first: TemplateStringsArray,
        ...interpolations: SimpleInterpolation[]
    ) => css`
        @media (max-width: ${breakpoint}px) {
            ${css(first, ...interpolations)}
        }
    `;
}

export const breakPoints = {
    lg: mediaMaxWidth(1200),
    md: mediaMaxWidth(900),
    sm: mediaMaxWidth(600),
    xs: mediaMaxWidth(500)
};

const pageShell = {
    minWidth: 399,
    minHeight: 760,
    desktop: {
        paddingX: 128,
        gap: 48,
    },
    mobile: {
        paddingX: 40,
        paddingY: 32,
        gap: 24,
    },
};

const header = {
    desktop: {
        width: 175,
        height: 30,
        logoWidth: 156,
    },
    mobile: {
        width: 125,
        height: 21,
        logoWidth: 156,
    }
};

const GlobalStyles = createGlobalStyle`
    @font-face {
        font-family: "Post Grotesk";
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url("${postGroteskBook}");
        ascent-override: 90%;
        descent-override: 22%;
    }

    @font-face {
        font-family: "Post Grotesk";
        font-style: normal;
        font-weight: 500;
        font-display: swap;
        src: url("${postGroteskMedium}");
        ascent-override: 90%;
        descent-override: 22%;
    }

    @font-face {
        font-family: "Roboto";
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        src: url("${robotoRegular}");
    }
`;

const PageShell = styled.main<{ $opaqueBackground?: boolean }>`
    align-items: center;
    background: ${({ $opaqueBackground }) => $opaqueBackground ? "#f0f3f5" : "rgba(0, 0, 0, 0.4)"};
    color: #001a24;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    -webkit-font-smoothing: antialiased;
    font-family: ${postGroteskFont};
    gap: ${pageShell.desktop.gap}px;
    height: 100dvh;
    justify-content: safe center;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: none;
    position: relative;
    width: 100%;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    ${breakPoints.xs`
        gap: ${pageShell.mobile.gap}px;
        height: 100dvh;
        overflow: auto;
        padding-top: ${pageShell.mobile.paddingY}px;
    `};
`;

export const PageBackground = styled.div.attrs({
    "aria-hidden": true
})`
    background:
        radial-gradient(ellipse 780px 540px at calc(50% - 302px) calc(50% + 223px), rgba(82, 208, 255, 0.54), rgba(82, 208, 255, 0) 68%),
        radial-gradient(ellipse 740px 540px at calc(50% + 290px) calc(50% + 178px), rgba(163, 190, 255, 0.38), rgba(163, 190, 255, 0) 70%),
        radial-gradient(ellipse 620px 440px at calc(50% + 430px) calc(50% + 446px), rgba(219, 200, 238, 0.58), rgba(219, 200, 238, 0) 72%),
        radial-gradient(ellipse 900px 360px at calc(50% + 72px) calc(50% - 470px), rgba(219, 238, 244, 0.68), rgba(219, 238, 244, 0) 72%);
    inset-block: 0;
    left: 50%;
    opacity: 0.8;
    pointer-events: none;
    position: fixed;
    transform: translateX(-50%);
    width: max(100vw, 1920px);
    height: 100dvh;

    ${breakPoints.xs`
        inset-inline: 0;
        transform: none;
        width: auto;
    `};
`;

const SharedFrame = styled.div`
    position: relative;
    width: calc(100vw - ${pageShell.desktop.paddingX}px);
    z-index: 1;

    ${breakPoints.xs`
        width: calc(100vw - ${pageShell.mobile.paddingX}px);
        min-width: ${pageShell.minWidth - 40}px
    `};
`;

const HeaderFrame = styled(SharedFrame).attrs({
    as: "header"
}) <{ hideHeaderMobile?: boolean, hideHeaderAlways?: boolean }>`
    align-items: flex-start;
    display: ${({ hideHeaderAlways }) => (hideHeaderAlways ? "none" : "flex")};
    height: ${header.desktop.height}px;
    justify-content: center;

    ${breakPoints.xs`
        align-items: center;
        height: ${header.mobile.height}px;
    `}

    ${({ hideHeaderMobile }) => hideHeaderMobile && breakPoints.xs`
        display: none;
    `}
`;

const LogoFrame = styled.div`
    align-items: center;
    display: flex;
    height: ${header.desktop.height}px;
    justify-content: center;
    width: ${header.desktop.width}px;

    ${breakPoints.xs`
        height: ${header.mobile.height}px;
        width: ${header.mobile.width}px;
    `}
`;

const ContentFrame = styled(SharedFrame)`
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    justify-content: center;
    min-height: 200px;
`;

export const PrimaryButton = styled.button`
    align-items: center;
    background: #52d0ff;
    border: 1px solid #52d0ff;
    border-radius: 6px;
    color: #001a24;
    cursor: pointer;
    display: flex;
    font-family: ${postGroteskFont};
    font-size: 18px;
    font-weight: 500;
    height: 44px;
    justify-content: center;
    line-height: 1.35;
    padding: 10px 24px;

    &:hover {
        background: #8EE0FF;
    }
`;

const StyledLogo = styled.img`
    display: block;
    height: auto;
    width: ${header.desktop.logoWidth}px;

    ${breakPoints.xs`
        width: ${header.mobile.logoWidth}px;
    `}
`;

export function Logo() {
    return (<StyledLogo
        alt="Beautiful.ai"
        src={beautifulAiLogo}
    />);
}

export function OnboardingPageStructure({ initialized, hideHeaderAlways, hideHeaderMobile, opaqueBackground = true, children }: {
    initialized: boolean;
    hideHeaderMobile?: boolean;
    hideHeaderAlways?: boolean;
    opaqueBackground?: boolean;
    children?: React.ReactNode;
}) {
    return (
        <>
            <GlobalStyles />
            <PageShell $opaqueBackground={opaqueBackground}>
                {opaqueBackground && <PageBackground />}
                <HeaderFrame hideHeaderMobile={hideHeaderMobile} hideHeaderAlways={hideHeaderAlways}>
                    <LogoFrame>
                        <Logo />
                    </LogoFrame>
                </HeaderFrame>
                {initialized
                    ? <ContentFrame>
                        {children}
                    </ContentFrame>
                    : <ContentFrame>
                        <Spinner />
                    </ContentFrame>}

            </PageShell>
        </>
    );
}
